import { useMemo } from 'react';

import { useGetOptionsForCLA } from 'hooks/useGetOptionsForCLA';
import { useTranslation } from 'react-i18next';

import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';
import { booleanValueGetter } from 'components/shared/DataGrid/utils/valueGetters/booleanValueGetter';
import { nestedFieldValueGetter } from 'components/shared/DataGrid/utils/valueGetters/nestedFieldValueGetter';
import { dateFormatter } from 'utils/grid';

import { GridColDef } from '@mui/x-data-grid-pro';

import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

import { useGetBuhHandlingOptions } from '../components/Sections/Surcharges/hooks/useGetBuhHandlingOptions';

export const useGetColumnDefs = (): GridColDef[] => {
  const { t } = useTranslation('common');

  const claOptions = useGetOptionsForCLA();
  const buhHandlingOptions = useGetBuhHandlingOptions();

  return useMemo(
    () => [
      {
        field: 'DEFAULT',
        type: 'boolean',
        headerName: t('employerCostSettings.grid.flags.DEFAULT'),
        valueGetter: booleanValueGetter,
        renderCell: BooleanCell,
        minWidth: 80
      },
      {
        field: 'name',
        headerName: t('employerCostSettings.grid.name'),
        type: 'string',
        hideable: false,
        sortable: false,
        minWidth: 200,
        flex: 1
      },
      {
        field: 'nameBrief',
        type: 'string',
        headerName: t('employerCostSettings.grid.nameBrief'),
        minWidth: 200,
        flex: 1
      },
      {
        field: 'claId',
        type: 'singleSelect',
        headerName: t('employerCostSettings.grid.claId'),
        valueOptions: claOptions,
        valueFormatter: (params) =>
          singleSelectValueFormatter(params, claOptions),
        minWidth: 100
      },
      {
        field: 'buhHandlingSvc',
        type: 'singleSelect',
        headerName: t('employerCostSettings.grid.buhHandlingSvc'),
        valueOptions: buhHandlingOptions,
        valueFormatter: (params) =>
          singleSelectValueFormatter(params, buhHandlingOptions),
        minWidth: 250,
        flex: 1
      },
      {
        field: 'valid.from',
        valueGetter: nestedFieldValueGetter,
        valueFormatter: dateFormatter,
        type: 'date',
        headerName: t('validFrom'),
        minWidth: 100
      },
      {
        field: 'valid.to',
        valueGetter: nestedFieldValueGetter,
        valueFormatter: dateFormatter,
        type: 'date',
        headerName: t('validTo'),
        minWidth: 100
      }
    ],
    [t, claOptions, buhHandlingOptions]
  );
};
